/**
 * Step2「字段配置」—— 维度区域：区块标题、内联新增自定义维度、维度表。
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button, Input, Select, Table, Text } from 'tea-component';

import {
  buildEmptyCustomRowEdit,
  isDimInlineAddDraftValid,
} from '../../../utils/fieldConfigRowUtils';
import { coerceDimTypeSelectValue } from '../constants';
import { useDimInlineFieldErrors } from '../hooks/useDimInlineFieldErrors';
import FieldConfigBatchSelectCheckbox from './FieldConfigBatchSelectCheckbox';
import { FieldConfigRequiredLabel } from './FieldConfigRequiredLabel';
import type { IDimensionSectionProps } from '../types';

/** Step2 维度区域 UI（无业务状态，仅展示与触发回调） */
const DimensionSection: React.FC<IDimensionSectionProps> = ({
  dimRows,
  dimColumns,
  dimInlineOpen,
  changeDimInlineOpen,
  dimAddDraft,
  changeDimAddDraft,
  dimTypeOptions,
  onConfirmDimInline,
  allDimSourceColumns,
  fieldsValue,
  changeFieldsValue,
  secError = false,
}) => {
  const { formatMessage } = useIntl();
  const {
    isInlineFieldError,
    clearInlineFieldError,
    validateOnConfirm,
  } = useDimInlineFieldErrors(dimInlineOpen);

  /** 内联「确认添加」：先必填再示例值格式，与表格行校验口径一致 */
  const handleConfirmDimInlineClick = (): void => {
    if (!isDimInlineAddDraftValid(dimAddDraft)) {
      return;
    }
    if (!validateOnConfirm(dimAddDraft)) {
      return;
    }
    onConfirmDimInline();
  };

  /** 维度表无数据时 Tea Table `topTip` 展示空态文案 */
  const dimEmptyTip =
    dimRows.length === 0
      ? formatMessage({ id: 'dbl_model_agent_step2_empty_dim' })
      : null;

  return (
    <section
      className={classnames('dbl-field-config-sec dbl-field-config-sec--dim', {
        'dbl-field-config-sec--error': secError,
      })}
    >
      <div className="dbl-field-config-sec-header">
        <Text className="dbl-field-config-sec-title">
          {formatMessage({ id: 'dbl_model_agent_step2_sec_dimensions' })}
        </Text>
        <Text className="dbl-field-config-sec-count">
          {formatMessage(
            { id: 'dbl_model_agent_summary_count_unit' },
            { n: dimRows.length },
          )}
        </Text>
        <div className="dbl-field-config-sec-header-add-group">
          <FieldConfigBatchSelectCheckbox
            variant="toolbar"
            batchKeys={allDimSourceColumns}
            fieldsValue={fieldsValue}
            changeFieldsValue={changeFieldsValue}
            formatMessage={formatMessage}
          />
          <Button
            type="weak"
            className="dbl-field-config-add-field-btn"
            onClick={() => {
              changeDimInlineOpen((open) => {
                if (open) {
                  return false;
                }
                changeDimAddDraft(buildEmptyCustomRowEdit('dimension'));
                return true;
              });
            }}
          >
            {formatMessage({ id: 'dbl_model_agent_step2_add_dimension' })}
          </Button>
        </div>
      </div>
      {dimInlineOpen ? (
        <div className="dbl-field-config-inline-add dbl-field-config-inline-add--dim">
          <Text
            className="dbl-field-config-inline-add__title"
          >
            {formatMessage({ id: 'dbl_model_agent_step2_inline_dim_heading' })}
          </Text>
          <div className="dbl-field-config-inline-add__row">
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_display' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={dimAddDraft.displayName}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, displayName: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_dim_display',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_name_zh' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={dimAddDraft.nameZh}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, nameZh: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_zh',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_name_en' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={dimAddDraft.nameEn}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, nameEn: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_en',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--narrow">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_data_type' })}
              </FieldConfigRequiredLabel>
              <Select
                size="m"
                appearance="button"
                options={dimTypeOptions}
                value={coerceDimTypeSelectValue(dimAddDraft.dataType)}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, dataType: String(v) }))
                }
              />
            </div>
          </div>
          <div className="dbl-field-config-inline-add__row">
            <div className="dbl-field-config-inline-add__field dbl-field-config-inline-add__field--grow">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_sql' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={dimAddDraft.sql}
                onChange={(v) => changeDimAddDraft((d) => ({ ...d, sql: v }))}
                placeholder={formatMessage(
                  {
                    id: 'dbl_model_agent_step2_inline_sql_placeholder_dim',
                  },
                  { CUBE: '{CUBE}' },
                )}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_desc' })}
              </FieldConfigRequiredLabel>
              <Input
                size="full"
                value={dimAddDraft.description}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, description: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_description',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <FieldConfigRequiredLabel variant="inline" required>
                {formatMessage({ id: 'dbl_model_agent_step2_col_sample' })}
              </FieldConfigRequiredLabel>
              <div
                className={classnames(
                  'dbl-field-config-inline-add__field-input-wrap',
                  {
                    'dbl-field-config-inline-add__field-input-wrap--error':
                      isInlineFieldError('sampleValues'),
                  },
                )}
              >
                <Input
                  size="full"
                  value={dimAddDraft.sampleValues}
                  onChange={(v) => {
                    changeDimAddDraft((d) => ({ ...d, sampleValues: v }));
                    clearInlineFieldError('sampleValues');
                  }}
                  placeholder={formatMessage({
                    id: 'dbl_model_agent_step2_placeholder_sample_dim',
                  })}
                />
              </div>
            </div>
            <div className="dbl-field-config-inline-add__field">
              <Text className="dbl-field-config-inline-add__label">
                {formatMessage({ id: 'dbl_model_agent_step2_col_dim_summary' })}
              </Text>
              <Input
                size="full"
                value={dimAddDraft.dimSummary}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, dimSummary: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_dim_summary',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <Text className="dbl-field-config-inline-add__label">
                {formatMessage({ id: 'dbl_model_agent_step2_col_dim_table' })}
              </Text>
              <Input
                size="full"
                value={dimAddDraft.dimTable}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, dimTable: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_dim_table',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__field">
              <Text className="dbl-field-config-inline-add__label">
                {formatMessage({ id: 'dbl_model_agent_step2_col_dim_join_column' })}
              </Text>
              <Input
                size="full"
                value={dimAddDraft.dimJoinColumn}
                onChange={(v) =>
                  changeDimAddDraft((d) => ({ ...d, dimJoinColumn: v }))
                }
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_dim_join',
                })}
              />
            </div>
            <div className="dbl-field-config-inline-add__actions">
              <Button
                type="primary"
                disabled={!isDimInlineAddDraftValid(dimAddDraft)}
                onClick={handleConfirmDimInlineClick}
              >
                {formatMessage({ id: 'dbl_model_agent_step2_modal_confirm' })}
              </Button>
              <Button type="weak" onClick={() => changeDimInlineOpen(false)}>
                {formatMessage({ id: 'dbl_model_agent_step2_modal_cancel' })}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="dbl-field-config-table-wrap">
        <Table
          bordered
          records={dimRows}
          columns={dimColumns}
          recordKey={(c) => c.source_column}
          topTip={dimEmptyTip}
          addons={[
            Table.addons.scrollable({
              maxHeight: 360,
              minWidth: 2310,
            }),
          ]}
        />
      </div>
    </section>
  );
};

export default DimensionSection;
